import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { OmniReportContentType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { OMNI_APPS_BASE_URL, OMNI_DASHBOARDS_BASE_URL } from '~constants/consts';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import { OmniReport, ReportCategory } from '~hooks/useOmniReports/models';
import { useOmniReports } from '~hooks/useOmniReports/useOmniReports';

import OmniReportDialog from './OmniReportDialog';

const contentTypeLabelByContentType: Record<OmniReportContentType, string> = {
  [OmniReportContentType.APP]: t('administration.report_settings.content_type_app'),
  [OmniReportContentType.BUILDER]: t(
    'administration.report_settings.content_type_builder',
  ),
  [OmniReportContentType.DASHBOARD]: t(
    'administration.report_settings.content_type_dashboard',
  ),
};

interface ReportCategoryContentProps {
  selectedReportCategory: ReportCategory | null;
  companyId: string;
}

export const ReportCategoryContent = ({
  selectedReportCategory,
  companyId,
}: ReportCategoryContentProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<OmniReport | null>(null);
  const [omniReports, setOmniReports] = useState<OmniReport[]>([]);
  const { getOmniReportsByReportCategory, deleteOmniReport } = useOmniReports();
  const [isLoading, setIsLoading] = useState(false);
  const fetchOmniReports = async () => {
    const omniReports = await getOmniReportsByReportCategory({
      companyId,
      reportCategoryId: selectedReportCategory?.id ?? '',
    });
    setOmniReports(omniReports?.data ?? []);
  };

  useEffect(() => {
    if (selectedReportCategory?.id) {
      fetchOmniReports();
    }
  }, [selectedReportCategory?.id]);

  const handleCreateReport = (report: OmniReport) => {
    setOmniReports([report, ...omniReports]);
  };

  const handleUpdateReport = (report: OmniReport) => {
    setOmniReports(omniReports.map((r) => (r.id === report.id ? report : r)));
  };

  const handleConfirmDelete = useCallback(async () => {
    if (selectedReport) {
      try {
        setIsLoading(true);
        await deleteOmniReport(companyId, selectedReport.id);
        setOmniReports(omniReports.filter((r) => r.id !== selectedReport.id));
      } catch (error) {
        console.error(error);
      } finally {
        setSelectedReport(null);
        setIsLoading(false);
        setIsDeleteDialogOpen(false);
      }
    }
  }, [companyId, selectedReport, omniReports]);

  const handleDelete = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const actionsOptions = (omniReport: OmniReport) => [
    {
      title: t('actions.delete'),
      icon: <Delete />,
      callBack: () => {
        setSelectedReport(omniReport);
        handleDelete();
      },
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: 'none',
        mt: 2,
        width: '100%',
      }}
    >
      {selectedReportCategory && (
        <>
          <Box
            sx={{
              m: 2,
              alignSelf: 'flex-end',
            }}
          >
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedReport(null);
                setIsDialogOpen(true);
              }}
              sx={{ width: 'fit-content' }}
            >
              {t('administration.report_settings.add_report')}
            </Button>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 4fr 1fr',
                gap: 2,
                backgroundColor: (theme) => theme.semantic.surfaceSunken,
                p: 1.5,
                borderRadius: (theme) =>
                  `${theme.spacing(0.5)} ${theme.spacing(0.5)} 0 0`,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                {t('form_fields.name')}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                {t('administration.report_settings.content_type')}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                {t('form_fields.omni_id')}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, textAlign: 'right' }}>
                {t('actions.actions')}
              </Typography>
            </Box>

            {/* Rows */}
            {omniReports.map((omniReport) => {
              const reportIsApp = omniReport.isApp;
              const reportIsBuilder = omniReport.isBuilder;
              const omniLinkBaseUrl = reportIsApp
                ? OMNI_APPS_BASE_URL
                : OMNI_DASHBOARDS_BASE_URL;
              const contentTypeLabel =
                contentTypeLabelByContentType[omniReport.contentType];

              return (
                <Box
                  key={omniReport.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 1fr 4fr 1fr',
                    gap: 2,
                    alignContent: 'center',
                    alignItems: 'center',
                    height: '48px',
                    p: 1.5,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {omniReport.name}
                    {omniReport.system && (
                      <Badge
                        sx={{
                          mx: 1,
                          '& .MuiBadge-badge': {
                            position: 'relative',
                            transform: 'none',
                            fontSize: '12px',
                            height: '20px',
                            padding: '0 6px',
                            backgroundColor: (theme) => theme.brandV2.colors.treadGray8,
                            color: (theme) => theme.brandV2.colors.treadGray2,
                            border: (theme) =>
                              `1px solid ${theme.brandV2.colors.treadGray7}`,
                          },
                        }}
                        badgeContent={t('common.system')}
                      />
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {contentTypeLabel}
                  </Typography>

                  {reportIsBuilder ? (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {omniReport.contentId}
                    </Typography>
                  ) : (
                    <Link
                      href={`${omniLinkBaseUrl}/${omniReport.contentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {omniReport.contentId}
                    </Link>
                  )}

                  {/* Actions */}
                  {(!omniReport.system ||
                    (omniReport.system && companyId === TREAD_UUID)) && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setSelectedReport(omniReport);
                          setIsDialogOpen(true);
                        }}
                        size="small"
                      >
                        <Edit />
                      </IconButton>

                      <SimpleMenu
                        options={actionsOptions(omniReport)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                      >
                        <MoreHoriz />
                      </SimpleMenu>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <OmniReportDialog
            open={isDialogOpen}
            onClose={() => {
              setIsDialogOpen(false);
              setSelectedReport(null);
            }}
            onSuccess={(report) => {
              if (selectedReport) {
                handleUpdateReport(report);
              } else {
                handleCreateReport(report);
              }
              setIsDialogOpen(false);
              setSelectedReport(null);
            }}
            companyId={companyId}
            defaultOmniReport={selectedReport}
            reportCategory={selectedReportCategory}
          />

          <ConfirmationDialog
            isBusy={isLoading}
            isOpen={isDeleteDialogOpen}
            onClose={() => {
              setIsDeleteDialogOpen(false);
              setSelectedReport(null);
            }}
            onConfirm={handleConfirmDelete}
            title={t('administration.report_settings.delete_report_title')}
          >
            <Typography>
              {t('administration.report_settings.delete_report_description')}
            </Typography>
          </ConfirmationDialog>
        </>
      )}
    </Box>
  );
};
